import { Component, EventEmitter, Input, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import {
  EstadoClienteParcela,
  EstadoContadorParcela,
  ParcelaContratoConsultaDTO,
} from '../../models/contador/parcela-contrato.model';
import { PeriodoFiltro, TipoPeriodo } from '../../models/dashboard/periodo-filtro.model';

/**
 * Grid APRESENTACIONAL ("burro") das parcelas do contrato.
 *
 * Renderiza a tabela (paginada/ordenada server-side), os filtros (periodo +
 * situacao) e — quando `mostrarAcoesContador` — a barra de selecao em lote
 * (Faturar/Excluir) e o botao "Adicionar parcelas". NAO busca dados nem executa
 * acoes de escrita: recebe tudo por `@Input` e emite eventos (`@Output`) para o
 * componente pai, que mantem o estado (paginacao/ordenacao/selecao) e as acoes.
 *
 * Uso:
 * - CONTADOR (Operar Cliente): `mostrarAcoesContador = true` — grid com selecao
 *   em lote, botoes de acao e filtro por `estadoContador`.
 * - CLIENTE (Notificacoes do Contador): `mostrarAcoesContador = false` — grid
 *   SOMENTE LEITURA (sem selecao/acoes) e filtro por `estadoCliente`.
 *
 * Reusa as chaves de i18n existentes do bloco do Operar Cliente
 * (`contador_operar.despesa_acessoria.parcelas.*`).
 */
@Component({
  selector: 'app-parcelas-contrato',
  templateUrl: './parcelas-contrato.component.html',
  styleUrls: ['./parcelas-contrato.component.scss'],
})
export class ParcelasContratoComponent {
  /** Conteudo da pagina atual (Page.content). */
  @Input() parcelas: ParcelaContratoConsultaDTO[] = [];
  @Input() carregando = false;
  @Input() erro = false;

  /** Total de parcelas do contrato (denominador da coluna "N"); `null` = fallback so-numero. */
  @Input() totalParcelasContrato: number | null = null;

  /**
   * Tooltip opcional (ⓘ) exibido ao lado do TITULO do card. Quando `undefined`
   * (default), NENHUM icone e renderizado — a visao do CONTADOR (Operar Cliente)
   * fica inalterada (regressao zero). A tela do CLIENTE (Notificacoes do Contador)
   * passa o texto explicativo das parcelas.
   */
  @Input() tituloTooltip?: string;
  /** Rotulo acessivel (aria-label) do icone de ajuda do titulo; fallback: `tituloTooltip`. */
  @Input() tituloTooltipAria?: string;

  // Toggles de visibilidade de colunas (default `true` = coluna visivel). Cada
  // lado desliga um toggle diferente: o CLIENTE desliga `mostrarColunasContador`
  // para nao ver dados/IDs da contraparte contadora (RNF03); o CONTADOR (Operar
  // Cliente) desliga `mostrarSaldo`. O cliente mantem a coluna Saldo.
  /** Colunas da contraparte: estadoContador, titulo do contador e nota (link). */
  @Input() mostrarColunasContador = true;
  /** Coluna "Saldo" (quanto falta pagar). */
  @Input() mostrarSaldo = true;

  // Ordenacao (server-side, controlada pelo pai).
  @Input() sortField: string | null = 'numeroParcela';
  @Input() sortOrder = 1;
  @Output() sortChange = new EventEmitter<{ field?: string | string[]; order?: number }>();

  // Paginacao (server-side, controlada pelo pai).
  @Input() first = 0;
  @Input() rows = 10;
  @Input() totalRecords = 0;
  @Input() pageSizeOptions: { label: number; value: number }[] = [
    { label: 10, value: 10 },
    { label: 20, value: 20 },
    { label: 50, value: 50 },
  ];
  @Output() pageChange = new EventEmitter<{ page?: number; rows?: number }>();
  @Output() rowsChange = new EventEmitter<number>();

  // Filtros.
  @Input() periodoValorPadrao: TipoPeriodo = 'showAll';
  @Output() periodoChange = new EventEmitter<PeriodoFiltro>();
  @Input() situacao: string | null = null;
  @Input() situacaoOptions: { label: string; value: string | null }[] = [];
  @Output() situacaoChange = new EventEmitter<string | null>();

  /** Retry ao falhar a carga. */
  @Output() recarregar = new EventEmitter<void>();

  // Acoes do CONTADOR (so aparecem quando mostrarAcoesContador = true).
  @Input() mostrarAcoesContador = false;
  @Input() selecionadas: ParcelaContratoConsultaDTO[] = [];
  @Output() selecionadasChange = new EventEmitter<ParcelaContratoConsultaDTO[]>();
  @Input() faturaveis = false;
  @Input() excluiveis = false;
  @Input() processandoFaturamento = false;
  @Input() excluindoParcelas = false;
  @Output() adicionar = new EventEmitter<void>();
  @Output() faturar = new EventEmitter<void>();
  @Output() excluir = new EventEmitter<void>();

  /** Abrir a nota consolidada vinculada a parcela (o pai navega). */
  @Output() abrirNota = new EventEmitter<ParcelaContratoConsultaDTO>();

  constructor(private translate: TranslateService) {}

  /**
   * Colspan da mensagem de tabela vazia (recomputado conforme as colunas
   * visiveis). Colunas SEMPRE visiveis: numero, referencia, vencimento, valor,
   * estadoCliente (5). +selecao (contador), +saldo, +3 da contraparte contadora
   * (estadoContador, tituloContador, nota).
   */
  get colSpan(): number {
    let total = 5;
    if (this.mostrarAcoesContador) total += 1; // coluna de selecao (checkbox)
    if (this.mostrarSaldo) total += 1; // coluna saldo
    if (this.mostrarColunasContador) total += 3; // estadoContador + tituloContador + nota
    return total;
  }

  /** Guard de selecao usado pelo p-table ([rowSelectable] recebe {data}). */
  isParcelaSelecionavel = (event: { data: ParcelaContratoConsultaDTO }): boolean =>
    this.parcelaSelecionavel(event.data);

  /** Parcela cancelada em qualquer um dos lados nao e selecionavel. */
  parcelaSelecionavel(parcela: ParcelaContratoConsultaDTO): boolean {
    return parcela.estadoCliente !== 'CANCELADA' && parcela.estadoContador !== 'CANCELADA';
  }

  /** Rotulo da coluna "N": `numero/total` quando o total e conhecido. */
  numeroParcelaLabel(parcela: ParcelaContratoConsultaDTO): string {
    return this.totalParcelasContrato != null
      ? `${parcela.numeroParcela}/${this.totalParcelasContrato}`
      : `${parcela.numeroParcela}`;
  }

  /**
   * Competencia da parcela como `MM/yyyy`, a partir de `dataReferencia` (ISO
   * `yyyy-MM-dd`). Formatado por split de string para evitar o shift de fuso do
   * pipe `date`.
   */
  competenciaParcela(dataReferencia: string | null | undefined): string {
    if (!dataReferencia) {
      return '-';
    }
    const [ano, mes] = dataReferencia.split('-');
    return ano && mes ? `${mes}/${ano}` : dataReferencia;
  }

  /** Cor (severity do p-tag) da situacao do CLIENTE (lado a pagar). */
  estadoClienteSeverity(estado: EstadoClienteParcela): 'success' | 'warning' | 'danger' {
    switch (estado) {
      case 'PAGA':
        return 'success';
      case 'CANCELADA':
        return 'danger';
      default: // PENDENTE
        return 'warning';
    }
  }

  /** Rotulo localizado da situacao do CLIENTE. */
  estadoClienteLabel(estado: EstadoClienteParcela): string {
    const chave = `contador_operar.despesa_acessoria.parcelas.estado_cliente.${estado.toLowerCase()}`;
    const traduzido = this.translate.instant(chave);
    return traduzido === chave ? estado : traduzido;
  }

  /** Cor (severity do p-tag) da situacao do CONTADOR (lado a receber). */
  estadoContadorSeverity(estado: EstadoContadorParcela): 'success' | 'warning' | 'info' | 'danger' {
    switch (estado) {
      case 'RECEBIDA':
        return 'success';
      case 'FATURADA':
        return 'info';
      case 'CANCELADA':
        return 'danger';
      default: // PENDENTE
        return 'warning';
    }
  }

  /** Rotulo localizado da situacao do CONTADOR. */
  estadoContadorLabel(estado: EstadoContadorParcela): string {
    const chave = `contador_operar.despesa_acessoria.parcelas.estado_contador.${estado.toLowerCase()}`;
    const traduzido = this.translate.instant(chave);
    return traduzido === chave ? estado : traduzido;
  }
}
